import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { formatShortDate } from './analyticsHelpers';

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 18,
  padding: 16,
  border: '1px solid rgba(112, 63, 246, 0.08)',
  boxShadow: 'var(--premium-shadow-nested)',
};

const RangeButton = ({ value, label, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    data-testid={`growth-range-${value}`}
    style={{
      padding: '6px 12px',
      borderRadius: 999,
      border: '1px solid',
      borderColor: active ? 'var(--accent-purple-strong)' : 'rgba(0,0,0,0.08)',
      background: active ? 'var(--accent-purple-strong)' : '#FFFFFF',
      color: active ? '#FFFFFF' : '#444',
      fontSize: 12,
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 150ms ease',
    }}
  >
    {label}
  </button>
);

const RANGES = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 365, label: '12mo' },
];

export const GrowthChart = ({ data, range, onRangeChange, loading }) => {
  const totals = useMemo(() => {
    if (!Array.isArray(data)) return { guests: 0, hosts: 0, venues: 0 };
    return data.reduce(
      (acc, row) => ({
        guests: acc.guests + (row.guests || 0),
        hosts: acc.hosts + (row.hosts || 0),
        venues: acc.venues + (row.venues || 0),
      }),
      { guests: 0, hosts: 0, venues: 0 }
    );
  }, [data]);

  const chartData = useMemo(() => (data || []).map((row) => ({ ...row, dayLabel: formatShortDate(row.day) })), [data]);

  return (
    <div style={cardStyle} data-testid="growth-chart-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, margin: 0, color: '#000' }}>Signup Growth</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {RANGES.map((r) => (
            <RangeButton key={r.value} value={r.value} label={r.label} active={range === r.value} onClick={onRangeChange} />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap', fontSize: 12, color: '#666' }}>
        <span>Total guests: <strong style={{ color: '#7C3AED' }}>{totals.guests}</strong></span>
        <span>Total hosts: <strong style={{ color: '#00B341' }}>{totals.hosts}</strong></span>
        <span>New venues: <strong style={{ color: '#E52445' }}>{totals.venues}</strong></span>
      </div>
      <div style={{ width: '100%', height: 240 }} data-testid="growth-chart">
        {loading && (!chartData || chartData.length === 0) ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>Loading…</div>
        ) : (
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
              <XAxis dataKey="dayLabel" tick={{ fontSize: 10, fill: '#999' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#999' }} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: '1px solid #EEE', fontSize: 12 }}
                labelStyle={{ fontWeight: 700, color: '#333' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="guests" stroke="#7C3AED" strokeWidth={2} dot={false} name="Guests" />
              <Line type="monotone" dataKey="hosts" stroke="#00B341" strokeWidth={2} dot={false} name="Hosts" />
              <Line type="monotone" dataKey="venues" stroke="#E52445" strokeWidth={2} dot={false} name="Venues" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
